import { ChevronDown, ChevronRight, Info, Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "../../components/ui/button";
import { StatusBadge } from "../../components/ui/statusBadge";
import { Textarea } from "../../components/ui/textarea";
import { formatDatetime, usernameFromEmail, usernamesOnly } from "../../lib/format";
import { cn } from "../../lib/utils";
import { useReadOnly } from "../lock/ReadOnlyContext";
import type { StipulationView } from "./api/types";
import { useStipulationHistory } from "./api/useStipulationHistory";
import { BANK_STIP_CODES, type BankAccountOwnerOption } from "./bankAccountOwnerOptions";
import BankVerificationModal from "./BankVerificationModal";
import { fundingManualUrl } from "./fundingManual";
import OpenLoanCheckModal from "./OpenLoanCheckModal";
import StipulationActionMenu from "./StipulationActionMenu";
import { useStipulationRowActions } from "./useStipulationRowActions";

interface StipulationRowProperties {
  approvalId?: string;
  stip: StipulationView;
  bankAccountOwnerOptions: BankAccountOwnerOption[];
}

// One stipulation card: expand toggle, title, policy link, status badge, action
// menu, optional dealer-instruction note, activity line, and an expandable
// Instructions History. Selections + the note are staged in the edits store so
// the UI updates immediately, then persisted per-row through the BFF action API.
function StipulationRow({ approvalId, stip, bankAccountOwnerOptions }: StipulationRowProperties) {
  const { t } = useTranslation();
  const readOnly = useReadOnly();
  const [expanded, setExpanded] = useState(false);
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [openLoanCheckModalOpen, setOpenLoanCheckModalOpen] = useState(false);
  const {
    currentLabel,
    note,
    porExceptionChecked,
    isSaving,
    isPorSaving,
    showNoteEditor,
    noteActionCode,
    noteReasonPk,
    handleSelect,
    persistAction,
    setNote,
    handlePorExceptionChange,
  } = useStipulationRowActions(approvalId, stip);

  const { data: fetchedHistory, isLoading: isHistoryLoading } = useStipulationHistory({
    approvalId: approvalId ?? "",
    stipCode: stip.stipCode,
    applicantPk: stip.applicantPk,
    enabled: expanded,
  });

  // Show pre-loaded list data immediately; replace with fresh fetch when available.
  const actionHistory = fetchedHistory ?? stip.history;

  // `isBankVerificationAllowed` is BFF-authoritative, so the Verify button can
  // appear on stipCodes outside the local BANK_STIP_CODES map (e.g. a new
  // proof-of-residence code added server-side before this map is updated).
  // Always scope options to this row's own applicant so an unrecognized code
  // never falls back to showing every applicant's owner options; only narrow
  // further by account type when the code is recognized. When the row has no
  // applicantPk to scope by, show no options rather than falling back to an
  // unfiltered (cross-applicant) list.
  const rowAccountType = BANK_STIP_CODES[stip.stipCode]?.accountType;
  const filteredOwnerOptions =
    stip.applicantPk === undefined
      ? []
      : bankAccountOwnerOptions.filter((option) => {
          if (option.applicantPk !== stip.applicantPk) {
            return false;
          }
          return !rowAccountType || option.accountType === rowAccountType;
        });

  return (
    <div className="border-border rounded border border-l-2 p-1.5">
      <div className="flex items-start gap-1.5">
        <button
          type="button"
          aria-expanded={expanded}
          aria-label={t("stipulations.toggleDetails")}
          onClick={() => setExpanded((previous) => !previous)}
          className="text-muted-foreground hover:text-foreground shrink-0 p-0.5"
        >
          {expanded ? (
            <ChevronDown aria-hidden="true" className="size-3.5" />
          ) : (
            <ChevronRight aria-hidden="true" className="size-3.5" />
          )}
        </button>
        <span
          className={cn(
            "min-w-0 flex-1 text-xs font-semibold",
            stip.isRedFlag ? "text-destructive" : "text-foreground"
          )}
        >
          {stip.title}
        </span>
      </div>

      {stip.availableActions.length > 0 ||
      stip.openLoanStip ||
      stip.secondaryProofsAllowed ||
      stip.policyTag ||
      stip.currentAction ? (
        <div className="mt-1 flex flex-col gap-1.5">
          <div className="flex flex-wrap-reverse items-center gap-2">
            {stip.availableActions.length > 0 && !readOnly && (
              <StipulationActionMenu
                actions={stip.availableActions}
                currentLabel={currentLabel}
                disabled={isSaving}
                onSelect={handleSelect}
              />
            )}
            {stip.isBankVerificationAllowed && (
              <Button
                type="button"
                variant="default"
                size="xs"
                onClick={() => setVerifyModalOpen(true)}
                className="h-5 px-1.5"
              >
                {t("stipulations.verify")}
              </Button>
            )}
            <div className="ml-auto flex shrink-0 items-center gap-2">
              {stip.policyTag && (
                <a
                  href={fundingManualUrl(stip.policyTag)}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={t("stipulations.policyAriaLabel")}
                  className="text-foreground hover:bg-accent rounded-full p-1"
                >
                  <Info aria-hidden="true" className="size-4" />
                </a>
              )}
              {stip.currentAction && (
                <StatusBadge
                  status="open"
                  label={stip.currentAction}
                  className={
                    stip.statusDisplay === "open"
                      ? "border-success-strong text-success-strong"
                      : "border-border text-muted-foreground"
                  }
                />
              )}
            </div>
          </div>
          {stip.secondaryProofsAllowed && (
            <label className="flex items-center gap-1.5 text-xs">
              <span>{t("stipulations.secondaryProofsLabel")}</span>
              <input
                type="checkbox"
                checked={porExceptionChecked}
                onChange={(event) => handlePorExceptionChange(event.target.checked)}
                disabled={isPorSaving || readOnly}
                className="size-4"
              />
            </label>
          )}
        </div>
      ) : null}

      {showNoteEditor && noteActionCode && (
        <div className="mt-1">
          <Textarea
            rows={2}
            value={note ?? ""}
            onChange={(event) => setNote(event.target.value)}
            onBlur={(event) => {
              persistAction(noteActionCode, noteReasonPk, event.target.value);
            }}
            placeholder={t("stipulations.dealerInstructionPlaceholder")}
            className="text-xs"
          />
        </div>
      )}

      {(stip.openLoanStip || (stip.recentNote && (stip.recentNote.actionName || stip.recentNote.auditInformation))) && (
        <div className="mt-1 flex items-start justify-between gap-2">
          {stip.recentNote && (stip.recentNote.actionName || stip.recentNote.auditInformation) && (
            <div className="text-muted-foreground text-xs leading-tight">
              <div className="flex gap-1">
                {stip.recentNote.actionName && (
                  <span className="text-primary font-semibold">{stip.recentNote.actionName}</span>
                )}
                {stip.recentNote.auditInformation && (
                  <span className="text-2xs">{usernamesOnly(stip.recentNote.auditInformation)}</span>
                )}
              </div>
              {stip.recentNote.actionName && stip.recentNote.description && <div>{stip.recentNote.description}</div>}
            </div>
          )}
          {stip.openLoanStip && (
            <Button
              type="button"
              variant="default"
              size="xs"
              onClick={() => setOpenLoanCheckModalOpen(true)}
              className="ml-auto h-5 shrink-0 px-1.5"
            >
              {t("stipulations.openLoanCheck")}
            </Button>
          )}
        </div>
      )}

      {expanded && (
        <fieldset className="border-border mt-1.5 rounded border p-1.5">
          <legend className="text-foreground px-1 text-xs font-semibold">
            {t("stipulations.instructionsHistory")}
          </legend>
          {isHistoryLoading && actionHistory.length === 0 ? (
            <div className="text-muted-foreground flex items-center gap-1 text-xs">
              <Loader2 aria-hidden="true" className="size-3 animate-spin" />
              {t("stipulations.historyLoading")}
            </div>
          ) : (
            actionHistory &&
            actionHistory.length > 0 && (
              <ul className="text-muted-foreground mb-1 flex flex-col gap-0.5 text-xs">
                {actionHistory.map((entry, index) => (
                  <li key={index} className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-1">
                      {entry.actionName && <span className="text-primary font-bold">{entry.actionName}</span>}
                      <span className="text-2xs">
                        {[
                          entry.performedAt ? formatDatetime(entry.performedAt) : undefined,
                          entry.performedBy ? usernameFromEmail(entry.performedBy) : undefined,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </div>
                    {(entry.reasonDescription ?? entry.noteText) && (
                      <div>{entry.reasonDescription ?? entry.noteText}</div>
                    )}
                  </li>
                ))}
              </ul>
            )
          )}
        </fieldset>
      )}

      {stip.isBankVerificationAllowed && (
        <BankVerificationModal
          open={verifyModalOpen}
          onOpenChange={setVerifyModalOpen}
          approvalId={approvalId}
          ownerOptions={filteredOwnerOptions}
        />
      )}

      {stip.openLoanStip && (
        <OpenLoanCheckModal
          open={openLoanCheckModalOpen}
          onOpenChange={setOpenLoanCheckModalOpen}
          approvalId={approvalId}
        />
      )}
    </div>
  );
}

export default StipulationRow;
